import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { resolve } from 'path';

import { doRequest } from '@r1-frontend/do-request';
import { IPacketDto } from '@r1-frontend/api-domru/api-epg/v1/dto/IPacketDto';
import { getPackageChannels } from '@r1-frontend/api-domru/api-epg/v1/packets';
import update, { ECategory, TResponseDto } from '@r1-frontend/api-domru/api-profile/v1/spec-offer/update';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import PopupOffer from '~/src/components/Promo/popups/offer';
import type { IChannels, IPromoItem } from '~/src/components/Promo/popups/PartnerPopup/types';
import { connectViaActivation } from '~/src/components/Promo/popups/PersonalOffersPopup/constants';
import PopupResponse from '~/src/components/Promo/popups/response';
import type { IProps as IPropsContainer } from '~/src/components/Promo/PromoContainer/types';
import type { TOverPopupProps } from '~/src/components/PromotionalCard/types';
import { getExpandedChannels } from '~/src/features/promo/utils';
import type { TState } from '~/src/store';
import { setStatusCard } from '~/src/store/actionCreators/promo';
import { getCurrentCity } from '~/src/store/selectors/city';
import { selectPromoStepId } from '~/src/store/selectors/promo';

type IProps = IPropsContainer['popupProps'] & TOverPopupProps;

export const PersonalOffersPopup:FC<IProps> = ({
    offers,
    item,
    offerPopupId,
    accessToken,
    sendVasGA,
    isPopupOpen,
    setIsPopupOpen,
    isConnected,
    category,
    stepId,
}) => {
    const router = useRouter();
    const dispatch = useDispatch();
    const noop = () => undefined;
    const [offer, setOffer] = useState<IPromoItem>(item);
    const paymentSum = useSelector((state: TState) => state.auth.clientPayment?.paySum);
    const [showResponsePopup, setShowResponsePopup] = useState(false);
    const [responseTitle, setResponseTitle] = useState('');
    const [responseText, setResponseText] = useState('');
    const [buttonText, setButtonText] = useState('');
    const [dispatchState, setDispatchState] = useState<() => void>(() => noop);
    const currentCity: ICurrentCity = useSelector(getCurrentCity);
    const providerId = useSelector((state: TState) => state?.city?.provider?.providerId);
    const selectStepId = useSelector((state: TState) => selectPromoStepId(state, item.id));
    const personalOffersPopup = ({ auth: { clientPayment, clientProducts, clientPersonal } }: TState) => ({
        payIsDeny: clientPayment?.payIsDeny,
        balance: clientPayment?.balance,
        paySum: clientPayment?.paySum,
        tariffPrice: Number(clientProducts?.tariffPrice),
        tariffPriceDiscount: Number(clientProducts?.tariffPriceDiscount),
        agreementNumber: clientPersonal?.agreement,
    });
    const { paySum: agreementNumber } = useSelector(personalOffersPopup);
    // TODO: Использовать после получения данных для обработки СП от биллинга
    // const promoList = useSelector((state: TState) => state.promo);

    const filteredOffersArray = useMemo(() => {
        return offers.filter(elem => elem.id === Number(offerPopupId));
    }, [offers]);

    useEffect(() => {
        setOffer(item);
    }, [item]);

    useEffect(() => {
        if (filteredOffersArray.length) {
            const [filteredOffers] = filteredOffersArray;
            setOffer(filteredOffers);
            setIsPopupOpen(true);
        }
    }, [filteredOffersArray]);

    useEffect(() => {
        isPopupOpen && showOfferInfo();
    }, [isPopupOpen]);

    async function pushOfferWrap() {
        if (!stepId) {
            return;
        }

        const { id } = item;

        const response = await doRequest.apiProfile<TResponseDto>(
            update(id, { currentStep: stepId }),
        );

        if (response.isSuccess) {
            dispatchStatusCardWrap(id, response.payload.category, response.payload.stepId);
        }
    }

    const showOfferPopup = async() => {
        (category === ECategory.new) && await pushOfferWrap();
    };

    const showOfferInfo = async() => {
        const { title, tvPackageId, channels, childOffers } = item;
        sendVasGA('click details btn', title);

        if (childOffers) {
            const promises = childOffers.map(offer => {
                if (offer.tvPackageId && tvPackageId) {
                    return doRequest.apiEPG<IPacketDto[]>(
                        getPackageChannels(providerId, {
                            packetIds: [tvPackageId],
                            active: 1,
                            with: 'channels',
                        }),
                    )
                    .then(response => {
                        if (response.isSuccess) {
                            const data: IChannels[] = getExpandedChannels(response.payload, tvPackageId);

                            setOffer(state => ({
                                ...state,
                                channels: data,
                            }));
                        }
                    });
                } else {
                    resolve();
                }
            });
            Promise.all(promises).then(() => {
                showOfferPopup();
            });
        } else if (tvPackageId && !channels) {
            const response = await doRequest.apiEPG<IPacketDto[]>(
                getPackageChannels(providerId, {
                    packetIds: [tvPackageId],
                    active: 1,
                    with: 'channels',
                }),
            );
            if (response.isSuccess) {
                const data: IChannels[] = getExpandedChannels(response.payload, tvPackageId);
                setOffer(state => ({
                    ...state,
                    channels: data,
                }));
            }
            await showOfferPopup();
        } else {
            await showOfferPopup();
        }
    };

    const closeResponsePopup = useCallback(() => {
        setShowResponsePopup(false);
        if (dispatchState) {
            dispatchState();
        }
    }, [dispatchState]);

    const onClickPopupButton = useCallback(() => {
        setShowResponsePopup(false);
        if (dispatchState) {
            dispatchState();
        }
        router.push(`/payments?pay=online&agreement=${agreementNumber}&s=${paymentSum}`);
    }, [dispatchState, paymentSum]);

    const dispatchStatusCardWrap = (id: number, category: ECategory, stepId: number) => {
        const isActiveWrap = category === ECategory.connected;
        const payload = {
            [id]: {
                isActive: isActiveWrap,
                category,
                stepId,
            },
        };
        dispatch(setStatusCard(payload));
    };

    const connectOffer = useCallback(async(child?: number) => {
        if (!selectStepId) {
            return;
        }

        const { title, id } = item;
        const isActivation = category !== ECategory.connected;

        sendVasGA(`click ${isActivation ? 'connect' : 'deactivation'} btn`, title);

        const response = await doRequest.apiProfile<TResponseDto>(
            update(id, { currentStep: selectStepId, child }),
        );

        if (response.isSuccess) {
            setDispatchState(() => () => dispatchStatusCardWrap(id, response.payload.category, response.payload.stepId));
            setIsPopupOpen(false);
            setShowResponsePopup(true);
            setResponseText(isActivation
                ? connectViaActivation.OFFERS_ACTIVATING_QUICKLY
                : connectViaActivation.OFFERS_DEACTIVATING_QUICKLY);
            setResponseTitle(isActivation
                ? connectViaActivation.ACTIVATED
                : connectViaActivation.DEACTIVATED);
            setButtonText('Выбрать еще');
            sendVasGA(`${isActivation ? 'connect' : 'deactivation'} success`, title);
        } else {
            setIsPopupOpen(false);
            setShowResponsePopup(true);
            setResponseText(response ? response.error.message : '');
            setResponseTitle(`Не удалось ${isActivation
                ? 'отключить'
                : 'подключить'} спецпредложение`);
            setButtonText('');
            sendVasGA(`${isActivation ? 'connect' : 'deactivation'} error`, title);
        }
    }, [item, selectStepId, category, sendVasGA, currentCity, accessToken, setIsPopupOpen]);

    return (
        <>
            <PopupOffer
                onClose={() => setIsPopupOpen(false)}
                isOpen={isPopupOpen}
                offer={offer}
                isConnected={isConnected}
                category={category}
                activate={connectOffer}
            />
            <PopupResponse
                isOpen={showResponsePopup}
                onClose={closeResponsePopup}
                title={responseTitle}
                text={responseText}
                buttonText={buttonText}
                wideButton={true}
                onButtonClick={onClickPopupButton}
            />
        </>
    );
};
